import { ReactElement } from 'react'
import { createFormKeyFromActivityAndQuestion } from '@/helpers/registration-check'
import { LocaleContext } from '@/providers/locale-provider'
import { Activity } from '@/types/activities'
import { Answer, AnswerResults, Question, QuestionsFormData, QuestionTypes } from '@/types/questions'
import { SelectInput, SelectOption } from '@lib-components'
import React, { ReactNode, use } from 'react'
import { Control, Controller, FieldValues } from 'react-hook-form'
import reactStringReplace from 'react-string-replace'

enum InputMatchingTokens {
    SINGLE_SELECT = '[INPUT:SingleSelect]',
    MULTI_SELECT = '[INPUT:MultiSelect]',
}
const REPLACE_DEFAULT_REGEX = /{DEFAULT}(.*?){\/DEFAULT}/

type FillInTheBlanksProps = {
    question: Question
    questions: Question[]
    activity: Activity
    control: Control<FieldValues, unknown>
    formData: QuestionsFormData
}

const FillInTheBlanks = ({ question, questions, activity, control, formData }: FillInTheBlanksProps): ReactElement => {
    const localeContext = use(LocaleContext)
    const { questionContent } = question

    /** Either removes the {DEFAULT} placeholders OR replaces that part of the question with the next question */
    const replaceDefaultText = (questionObject: Question): ReactNode[] => {
        const answerAndTargetQuestion = getAnswerAndTargetQuestion(questionObject.id)
        const text = localeContext.getLocaleText(questionObject.questionContent.text)

        /** If there is no answer/targetQuestion, simply remove the {DEFAULT} and {/DEFAULT} placeholders but keep the text in between */
        if (!answerAndTargetQuestion) {
            return reactStringReplace(text, REPLACE_DEFAULT_REGEX, (match) => match) as ReactNode[]
        }

        const { targetQuestionObject, answerObject } = answerAndTargetQuestion
        const key = `${QuestionTypes.FILL_IN_THE_BLANKS}-${questionObject.id}-${answerObject.id}-${targetQuestionObject.id}`

        /** Replace the {DEFAULT} placeholder with the target question "FillInTheBlanks" */
        return reactStringReplace(text, REPLACE_DEFAULT_REGEX, (_, index) => (
            <FillInTheBlanks
                question={targetQuestionObject}
                questions={questions}
                activity={activity}
                control={control}
                formData={formData}
                key={`${key}-${index}`}
            />
        )) as ReactNode[]
    }

    /** Replaces the SingleSelect placeholder with a "SingleSelect component" */
    const replaceSingleSelectPlaceholder = (text: ReactNode[]): ReactNode[] => {
        return reactStringReplace(text, InputMatchingTokens.SINGLE_SELECT, (_match, index) => (
            <Controller
                control={control}
                name={createFormKeyFromActivityAndQuestion(activity, question)}
                shouldUnregister
                render={({ field: { onChange, value } }) => (
                    <SelectInput
                        id={createFormKeyFromActivityAndQuestion(activity, question, index)}
                        key={createFormKeyFromActivityAndQuestion(activity, question, index)}
                        value={value}
                        onChange={(option: SelectOption) => {
                            onChange(option.value)
                        }}
                        options={questionContent.answers.map<SelectOption>((answer: Answer) => ({
                            value: answer.id,
                            label: localeContext.getLocaleText(answer.text),
                        }))}
                        isInline
                    />
                )}
            />
        )) as ReactNode[]
    }

    /** Replaces the MultiSelect placeholder with a "Multiselect component" */
    const replaceMultiSelectPlaceholder = (text: ReactNode[]): ReactNode[] => {
        return reactStringReplace(text, InputMatchingTokens.MULTI_SELECT, (_match, index) => (
            <Controller
                control={control}
                name={createFormKeyFromActivityAndQuestion(activity, question)}
                shouldUnregister
                render={({ field: { onChange, value } }) => (
                    <SelectInput
                        id={createFormKeyFromActivityAndQuestion(activity, question, index)}
                        key={createFormKeyFromActivityAndQuestion(activity, question, index)}
                        value={value}
                        onChange={(options: SelectOption[]) => {
                            onChange(options.map((option) => option.value))
                        }}
                        options={questionContent.answers.map<SelectOption>((answer: Answer) => ({
                            value: answer.id,
                            label: localeContext.getLocaleText(answer.text),
                        }))}
                        isInline
                        isMulti
                    />
                )}
            />
        )) as ReactNode[]
    }

    /** Returns nothing or an object containing the Answer-object and TargetQuestion-object of the given answer */
    const getAnswerAndTargetQuestion = (
        questionId: string,
    ): { targetQuestionObject: Question; answerObject: Answer } | null => {
        const questionObject = questions.find((questionFromArray: Question) => questionFromArray.id === questionId)
        const matchingFormData = formData[createFormKeyFromActivityAndQuestion(activity, questionObject)]
        /** If the question is not answered yet or when the question does not exist, return */
        if (!matchingFormData || !questionObject) return

        const answerObject = questionObject.questionContent.answers.find(
            (answer: Answer) => answer.id === matchingFormData,
        )
        /** If the answer does not exist or the answer result type is something different than EXTEND_QUESTION_TEXT, return */
        if (!answerObject || answerObject.result.type !== AnswerResults.EXTEND_QUESTION_TEXT) return

        const targetQuestionObject = questions.find(
            (questionFromArray: Question) => questionFromArray.id === answerObject.result.target,
        )
        /** If the targetQuestion does not exist, return */
        if (!targetQuestionObject) return

        return { targetQuestionObject, answerObject }
    }

    /** First replaces the {DEFAULT}-placeholder in the question text, then replaces the Input-placeholder */
    const getQuestionTextWithReplacements = (): ReactNode[] => {
        let questionTextWithReplacements = replaceDefaultText(question)
        questionTextWithReplacements = replaceSingleSelectPlaceholder(questionTextWithReplacements)
        questionTextWithReplacements = replaceMultiSelectPlaceholder(questionTextWithReplacements)

        return questionTextWithReplacements
            .filter((partial) => partial !== '')
            .map((partial: ReactNode, index: number) => (
                <span className="inline-flex gap-2 items-center" key={index}>
                    {partial}
                </span>
            ))
    }

    return <>{getQuestionTextWithReplacements()}</>
}
export default FillInTheBlanks
